'use client';

import { __assign } from '../_virtual/_tslib.js';
import React__default, { useReducer, useState, useRef, useEffect } from 'react';
import { DEFAULT_OPTIONS, DEFAULT_STYLE_OPTIONS, DEFAULT_TEXT_OPTIONS, ADDITION_HEIGHT, DEFAULT_MAX_PERCENT, DEFAULT_BORDER_RADIUS, DEFAULT_BAR_TEXT_GAP_BOTTOM, DEFAULT_HEIGHT_PADDING, DEFAULT_FONT_SIZE, DEFAULT_BAR_TEXT_GAP_TOP } from './constants.js';
import { BarChartWrapper, BarRect, AxisText, BarText, TooltipDot, TooltipText } from './BarChart.css.js';
import { calculateBarSpacing } from './helpers.js';
import Tooltip from './components/Tooltip/index.js';
import { barChartReducer, BarChartContext } from './context/reducer.js';
import { initialBarChart } from './context/index.js';
import { isMobile } from '../Utils/helpers.js';
import useElementResize from '../Utils/hooks/UseElementResize/index.js';

var BarChart = function (props) {
    var data = props.data, labels = props.labels, options = props.options, styleOptions = props.styleOptions, textOptions = props.textOptions, _a = props.highlight, highlight = _a === void 0 ? '' : _a;
    var _b = useReducer(barChartReducer, initialBarChart), barChartState = _b[0], dispatch = _b[1];
    var _c = useState(300), chartWidth = _c[0], setChartWidth = _c[1];
    var _d = useState(-1), hoverBarIndex = _d[0], setHoverBarIndex = _d[1];
    var wrapperRef = useRef(null);
    var elementSize = useElementResize((wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) || null)[0];
    var isInMobile = isMobile();
    var mergedOptions = __assign(__assign({}, DEFAULT_OPTIONS), options);
    var mergedStyleOptions = __assign(__assign({}, DEFAULT_STYLE_OPTIONS), styleOptions);
    var mergedTextOptions = __assign(__assign({}, DEFAULT_TEXT_OPTIONS), textOptions);
    var chartHeight = mergedStyleOptions.barHeight + ADDITION_HEIGHT;
    var displayLineCount = mergedStyleOptions.lineCount - 1;
    // line spacing is calculated by 100% / lineCount
    var lineSpacing = 100 / displayLineCount;
    var lineSpacingText = Math.round(lineSpacing);
    useEffect(function () {
        if (wrapperRef.current === null) {
            return;
        }
        setChartWidth(wrapperRef.current.offsetWidth);
    }, [wrapperRef, elementSize]);
    var renderTooltip = function () {
        var _a, _b;
        if (typeof window === 'undefined') {
            return null;
        }
        if (hoverBarIndex === -1) {
            return null;
        }
        var itemLabel = (_a = labels[hoverBarIndex]) !== null && _a !== void 0 ? _a : '';
        if (mergedTextOptions.tooltipDisplayValues && mergedTextOptions.tooltipDisplayValues.length > 0) {
            itemLabel = (_b = mergedTextOptions.tooltipDisplayValues[hoverBarIndex].toString()) !== null && _b !== void 0 ? _b : '';
        }
        var isHighLight = itemLabel === highlight;
        return (React__default.createElement(Tooltip, { chartRef: wrapperRef },
            React__default.createElement(TooltipDot, { bgColor: isHighLight ? mergedStyleOptions.highlightColor : mergedStyleOptions.barColor }),
            itemLabel,
            " ",
            mergedTextOptions.tooltipText,
            isHighLight && (React__default.createElement(TooltipText, { isYou: isHighLight, highlightColor: mergedStyleOptions.highlightColor },
                ' ',
                mergedTextOptions.highlightText))));
    };
    var renderBottomText = function (text, textX) {
        if (text !== 'Chưa xác định') {
            return text;
        }
        if (mergedStyleOptions.isRotateBottomText) {
            return text;
        }
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("tspan", { x: textX }, "Ch\u01B0a"),
            React__default.createElement("tspan", { x: textX, dy: '1.2em' }, "x\u00E1c \u0111\u1ECBnh")));
    };
    var chartLeftPadding = 32 + mergedStyleOptions.leftPadding;
    var chartTopPadding = mergedStyleOptions.topPadding;
    var chartBottomPadding = mergedStyleOptions.bottomPadding;
    var isSmaller1024 = true;
    if (typeof window !== 'undefined') {
        isSmaller1024 = window.innerWidth <= 1024;
    }
    return (React__default.createElement(BarChartContext.Provider, { value: {
            barChartState: barChartState,
            dispatch: dispatch
        } },
        React__default.createElement(BarChartWrapper, { ref: wrapperRef },
            React__default.createElement("svg", { width: chartWidth, height: chartHeight },
                React__default.createElement("g", { id: 'bars-bg' }, data.map(function (value, index) {
                    var itemBarHeight = (value * mergedStyleOptions.barHeight) / DEFAULT_MAX_PERCENT;
                    if (mergedStyleOptions.isShowWhenBarZero && itemBarHeight === 0) {
                        itemBarHeight = 1;
                    }
                    var barSpacing = calculateBarSpacing(mergedOptions.isHorizontal
                        ? mergedStyleOptions.barHeight
                        : chartWidth - mergedStyleOptions.rightPadding, mergedStyleOptions.barWidth, data.length, index, mergedTextOptions.xAxisPosition, mergedOptions.isHorizontal);
                    var barX = mergedStyleOptions.barWidth * index +
                        barSpacing +
                        (mergedOptions.isHorizontal ? chartTopPadding : chartLeftPadding);
                    var barY = DEFAULT_HEIGHT_PADDING + 32;
                    return (React__default.createElement("g", { key: index }, mergedStyleOptions.barBackgroundColor !== '' && (React__default.createElement(BarRect, { isHaveAnim: false, isHorizontal: mergedOptions.isHorizontal, x: mergedOptions.isHorizontal ? chartLeftPadding : barX, y: mergedOptions.isHorizontal ? barX : barY, width: mergedOptions.isHorizontal
                            ? chartWidth - chartLeftPadding - mergedStyleOptions.rightPadding
                            : mergedStyleOptions.barWidth, height: mergedOptions.isHorizontal ? mergedStyleOptions.barWidth : chartHeight, fill: mergedStyleOptions.barBackgroundColor, rx: DEFAULT_BORDER_RADIUS, ry: DEFAULT_BORDER_RADIUS }))));
                })),
                React__default.createElement("g", { id: 'grid' },
                    Array.from({ length: mergedStyleOptions.lineCount }).map(function (_, index) {
                        var barHeight = mergedOptions.isHorizontal
                            ? chartWidth - chartLeftPadding - mergedStyleOptions.rightPadding
                            : mergedStyleOptions.barHeight - chartBottomPadding;
                        var gapSpace = (barHeight / displayLineCount) * index;
                        var lineY = barHeight - gapSpace + DEFAULT_HEIGHT_PADDING + 32;
                        if (mergedOptions.isHorizontal) {
                            lineY = gapSpace + chartLeftPadding;
                        }
                        var lineText = index * lineSpacingText;
                        var renderLineText = (styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.renderYAxisText)
                            ? styleOptions.renderYAxisText(lineText)
                            : lineText;
                        var textX = chartHeight - DEFAULT_HEIGHT_PADDING + DEFAULT_BAR_TEXT_GAP_BOTTOM;
                        return (React__default.createElement("g", { key: "g-line-".concat(index) },
                            React__default.createElement("line", { x1: mergedOptions.isHorizontal ? lineY : chartLeftPadding, y1: mergedOptions.isHorizontal ? chartTopPadding : lineY, x2: mergedOptions.isHorizontal ? lineY : chartWidth, y2: mergedOptions.isHorizontal
                                    ? chartHeight - chartTopPadding - DEFAULT_BAR_TEXT_GAP_BOTTOM - 2
                                    : lineY, stroke: mergedStyleOptions.gridColor, strokeWidth: 1, strokeDasharray: "".concat(mergedStyleOptions.strokeDasharray.width, " ").concat(mergedStyleOptions.strokeDasharray.spacing) }),
                            React__default.createElement("text", { x: mergedOptions.isHorizontal ? lineY : 2, y: mergedOptions.isHorizontal ? textX : lineY, fill: mergedStyleOptions.labelColor, textAnchor: mergedOptions.isHorizontal ? 'middle' : 'start', fontSize: isInMobile
                                    ? mergedStyleOptions.mobileFontSize
                                    : mergedStyleOptions.desktopFontSize }, renderLineText)));
                    }),
                    React__default.createElement(AxisText, { x: 2, y: 16, fill: mergedStyleOptions.labelColor, textAnchor: 'start' }, mergedOptions.isHorizontal ? mergedTextOptions.xAxisText : mergedTextOptions.yAxisText)),
                React__default.createElement("g", { id: 'bars' },
                    data.map(function (value, index) {
                        var isHighLight = labels[index] === highlight;
                        var barHeight = mergedOptions.isHorizontal
                            ? chartWidth - chartLeftPadding - mergedStyleOptions.rightPadding
                            : mergedStyleOptions.barHeight - chartBottomPadding;
                        var itemBarHeight = (value * barHeight) / DEFAULT_MAX_PERCENT;
                        // Do not completely hide bar when it percent is zero
                        // Should keep it as 1 pixel
                        if (mergedStyleOptions.isShowWhenBarZero && itemBarHeight === 0) {
                            itemBarHeight = 1;
                        }
                        // Calculate spacing between bar item
                        var barSpacing = calculateBarSpacing(mergedOptions.isHorizontal
                            ? mergedStyleOptions.barHeight
                            : chartWidth - mergedStyleOptions.rightPadding, mergedStyleOptions.barWidth, data.length, index, mergedTextOptions.xAxisPosition, mergedOptions.isHorizontal);
                        // calculate bar position X
                        var barX = mergedStyleOptions.barWidth * index +
                            barSpacing +
                            (mergedOptions.isHorizontal ? chartTopPadding : chartLeftPadding);
                        // calculate bar position Y
                        var barY = mergedStyleOptions.barHeight -
                            itemBarHeight +
                            DEFAULT_HEIGHT_PADDING +
                            32 -
                            chartBottomPadding;
                        var textX = barX + mergedStyleOptions.barWidth / 2;
                        if (mergedOptions.isHorizontal) {
                            textX = barX + mergedStyleOptions.barWidth / 2 + DEFAULT_FONT_SIZE / 2 - 2;
                        }
                        // Fill bar color by highlight or not
                        var fillColor = isHighLight
                            ? mergedStyleOptions.highlightColor
                            : mergedStyleOptions.barColor;
                        var barOpacity = 1;
                        var overlayBarOpacity = 1;
                        var textOpacity = 1;
                        if (hoverBarIndex !== -1) {
                            barOpacity = hoverBarIndex === index ? 1 : 0.2;
                            textOpacity = hoverBarIndex === index ? 1 : 0.2;
                            overlayBarOpacity = hoverBarIndex === index ? 1 : 0;
                        }
                        var overflowBottomBarHeight = itemBarHeight - DEFAULT_BORDER_RADIUS;
                        var topTextPosX = mergedOptions.isHorizontal
                            ? chartLeftPadding + itemBarHeight + DEFAULT_BAR_TEXT_GAP_TOP
                            : textX;
                        if (topTextPosX < chartLeftPadding) {
                            // DEFAULT_BORDER_RADIUS is used for fixed value only (constant)
                            // No meaning in formular
                            topTextPosX = chartLeftPadding + DEFAULT_BORDER_RADIUS;
                        }
                        var horizontalBarWidth = itemBarHeight;
                        // Do not completely hide bar item when it percent is zero
                        // Should keep it as 1 pixel
                        if (itemBarHeight === 1 && horizontalBarWidth < chartLeftPadding) {
                            horizontalBarWidth = 1;
                        }
                        var bottomTextX = mergedOptions.isHorizontal ? 0 : textX;
                        var bottomTextY = mergedOptions.isHorizontal
                            ? textX
                            : chartHeight -
                                DEFAULT_HEIGHT_PADDING -
                                chartBottomPadding +
                                DEFAULT_BAR_TEXT_GAP_BOTTOM;
                        // If this chart is vertical chart and is rotated bottom text
                        // then we need to add 10 more pixels for margin-top
                        if (!mergedOptions.isHorizontal && mergedStyleOptions.isRotateBottomText) {
                            bottomTextX += 10;
                        }
                        var bottomTextAnchor = 'middle';
                        if (mergedOptions.isHorizontal) {
                            bottomTextAnchor = 'start';
                        }
                        else {
                            if (mergedStyleOptions.isRotateBottomText) {
                                bottomTextAnchor = 'end';
                            }
                        }
                        var barWidth = mergedOptions.isHorizontal ? horizontalBarWidth : mergedStyleOptions.barWidth;
                        if (barWidth < 0) {
                            barWidth = 0;
                        }
                        var overflowBottomBarWidth = mergedOptions.isHorizontal
                            ? overflowBottomBarHeight - mergedStyleOptions.rightPadding
                            : mergedStyleOptions.barWidth;
                        if (overflowBottomBarWidth < 0) {
                            overflowBottomBarWidth = 0;
                        }
                        var overflowBarWidth = mergedOptions.isHorizontal
                            ? chartLeftPadding
                            : mergedStyleOptions.barWidth;
                        if (overflowBarWidth < 0) {
                            overflowBarWidth = 0;
                        }
                        var bottomTextLabel = labels[index];
                        // splitString
                        var barHeightAnimationDuration = "".concat((value * 1.2) / 100, "s");
                        return (React__default.createElement("g", { key: index },
                            React__default.createElement(BarRect, { isHaveAnim: true, isHorizontal: mergedOptions.isHorizontal, opacity: barOpacity, x: mergedOptions.isHorizontal ? chartLeftPadding : barX, y: mergedOptions.isHorizontal ? barX : barY, width: barWidth, height: mergedOptions.isHorizontal ? mergedStyleOptions.barWidth : itemBarHeight, fill: fillColor, rx: DEFAULT_BORDER_RADIUS, ry: DEFAULT_BORDER_RADIUS, style: {
                                    animationDuration: barHeightAnimationDuration
                                }, onMouseOver: function () { return setHoverBarIndex(index); }, onMouseLeave: function () { return setHoverBarIndex(-1); }, onClick: function () { return setHoverBarIndex(index); } }),
                            overflowBottomBarHeight > 0 && (React__default.createElement(BarRect, { isHaveAnim: true, opacity: overlayBarOpacity, isHorizontal: mergedOptions.isHorizontal, x: mergedOptions.isHorizontal ? chartLeftPadding : barX, y: mergedOptions.isHorizontal ? barX : barY + DEFAULT_BORDER_RADIUS, width: overflowBottomBarWidth, height: mergedOptions.isHorizontal
                                    ? mergedStyleOptions.barWidth
                                    : overflowBottomBarHeight, fill: fillColor, style: {
                                    pointerEvents: 'none',
                                    animationDuration: barHeightAnimationDuration
                                } })),
                            mergedStyleOptions.isShowBarText ? (React__default.createElement(BarText, { isHorizontal: mergedOptions.isHorizontal, x: topTextPosX, y: mergedOptions.isHorizontal ? textX : barY - DEFAULT_BAR_TEXT_GAP_TOP, fill: isHighLight
                                    ? mergedStyleOptions.highlightColor
                                    : mergedStyleOptions.labelColor, opacity: textOpacity, textAnchor: mergedOptions.isHorizontal ? 'start' : 'middle', style: {
                                    fontWeight: isHighLight
                                        ? mergedStyleOptions.highlightFontWeight
                                        : mergedStyleOptions.labelFontWeight,
                                    animationDuration: barHeightAnimationDuration
                                }, fontSize: isInMobile
                                    ? mergedStyleOptions.mobileFontSize
                                    : mergedStyleOptions.desktopFontSize },
                                value,
                                mergedTextOptions.topLabelPostfixText)) : null,
                            React__default.createElement("rect", { x: mergedOptions.isHorizontal ? 0 - 2 : barX - 1, y: mergedOptions.isHorizontal ? barX - 1 : itemBarHeight + barY, fill: '#fff', width: overflowBarWidth + 2, height: mergedOptions.isHorizontal
                                    ? mergedStyleOptions.barWidth + 2
                                    : chartHeight -
                                        itemBarHeight -
                                        barY +
                                        mergedStyleOptions.bottomPadding +
                                        2 }),
                            React__default.createElement("g", null,
                                React__default.createElement("text", { x: bottomTextX, y: mergedOptions.isHorizontal ? textX : bottomTextY, fill: mergedStyleOptions.labelColor, textAnchor: bottomTextAnchor, transform: mergedStyleOptions.isRotateBottomText
                                        ? "rotate(-45 ".concat(bottomTextX, ",").concat(bottomTextY, ")")
                                        : '', fontSize: isInMobile
                                        ? mergedStyleOptions.mobileFontSize
                                        : mergedStyleOptions.desktopFontSize }, isSmaller1024
                                    ? bottomTextLabel
                                    : renderBottomText(bottomTextLabel, bottomTextX)))));
                    }),
                    React__default.createElement(AxisText, { x: mergedTextOptions.xAxisPosition === 'start' ? 0 : chartWidth, y: chartHeight - DEFAULT_HEIGHT_PADDING + DEFAULT_BAR_TEXT_GAP_BOTTOM, fill: mergedStyleOptions.labelColor, textAnchor: mergedTextOptions.xAxisPosition }, mergedOptions.isHorizontal ? mergedTextOptions.yAxisText : mergedTextOptions.xAxisText))),
            renderTooltip())));
};

export { BarChart as default };
//# sourceMappingURL=index.js.map
